import React from 'react';

import { Alert, Box, Button, Skeleton, Stack, Typography } from '@mui/material';

import { useGetLensCatalogueDataset } from '~/hooks/apis/reporting/useLensCatalogue';

import type { LensDataset } from '~/types/leLens';

import { describeGrain, formatAge, formatBytes } from './panelFormat';

/**
 * Region 2's facts line. A governed dataset shows the table, the catalogue's own description and
 * one line naming it "Governed dataset", how often it refreshes and how many fields it declares;
 * none of that is a dry run this dataset ran itself, so there is nothing here to re-run.  A
 * custom dataset (anything else, including the "not yet declared" legacy state: a dataset saved
 * before columns existed) keeps the SQL-era facts: grain, row count, bytes scanned, dry-run age,
 * and "Declare now" when it has never been typed.
 */
export default function DatasetFacts({
  dataset,
  rowCount,
  restricted,
  declaring,
  onDeclareNow,
  onOpenCatalogueDataset,
  mergeBanner,
}: {
  dataset: LensDataset | undefined;
  rowCount?: number;
  restricted?: boolean;
  declaring?: boolean;
  onDeclareNow?: () => void;
  /** "See full details in the Data catalogue": opens the catalogue's dataset detail screen.
   *  Governed datasets only; omitted means the link does not render (e.g. no mount-site wiring
   *  yet, or a Lens embedded somewhere the catalogue is not reachable from). */
  onOpenCatalogueDataset?: (catalogueId: string) => void;
  mergeBanner?: React.ReactNode;
}) {
  const isGoverned = dataset?.source?.kind === 'governed';
  const catalogueDetail = useGetLensCatalogueDataset(isGoverned ? dataset?.source?.catalogueId : undefined);

  if (!dataset) {
    return (
      <Stack spacing={0.5} sx={{ py: 0.5 }}>
        <Skeleton height={16} variant="text" width="70%" />
        <Skeleton height={16} variant="text" width="55%" />
      </Stack>
    );
  }

  if (isGoverned) {
    const description = catalogueDetail.data?.description;
    const refresh = catalogueDetail.data?.refresh;
    const fieldCount = catalogueDetail.data?.fieldCount ?? dataset.columns?.length;
    const line3 = [
      'Governed',
      refresh ? `refreshed ${refresh}` : undefined,
      fieldCount ? `${fieldCount} fields` : undefined,
    ]
      .filter(Boolean)
      .join(' · ');

    const catalogueId = dataset.source?.catalogueId;

    return (
      <Stack spacing={0.75} sx={{ py: 0.5 }}>
        <Typography color="text.secondary" sx={{ fontFamily: 'monospace' }} variant="caption">
          {dataset.source?.table ?? dataset.name}
        </Typography>
        {description && <Typography variant="body2">{description}</Typography>}
        <Typography color="text.secondary" variant="caption">
          {line3}
        </Typography>
        {catalogueId && onOpenCatalogueDataset && (
          <Button
            onClick={() => onOpenCatalogueDataset(catalogueId)}
            size="small"
            sx={{ alignSelf: 'flex-start', ml: -0.5 }}
          >
            Open in Data catalogue
          </Button>
        )}
        {restricted && (
          <Alert severity="warning" variant="outlined">
            Restricted. This card&rsquo;s data is blocked; the canvas shows Restricted instead of a preview.
          </Alert>
        )}
        {mergeBanner && <Box>{mergeBanner}</Box>}
      </Stack>
    );
  }

  const notDeclared = !dataset.columns?.length;

  return (
    <Stack spacing={0.75} sx={{ py: 0.5 }}>
      <Typography color="text.secondary" sx={{ fontFamily: 'monospace' }} variant="caption">
        {dataset.source?.table ?? dataset.name}
      </Typography>
      <Typography color="text.secondary" variant="caption">
        Custom query
      </Typography>

      {notDeclared ? (
        <Alert
          action={
            <Button disabled={declaring} onClick={onDeclareNow} size="small">
              {declaring ? 'Declaring…' : 'Declare now'}
            </Button>
          }
          severity="info"
          sx={{ alignItems: 'center' }}
          variant="outlined"
        >
          Columns not yet declared
          <Typography color="text.secondary" sx={{ display: 'block', mt: 0.25 }} variant="caption">
            Declare columns to use fields and filters.
          </Typography>
        </Alert>
      ) : (
        <>
          {describeGrain(dataset.grain?.keys) && (
            <Typography sx={{ fontFamily: 'monospace' }} variant="caption">
              {describeGrain(dataset.grain?.keys)}
              {!dataset.grain?.verified && ' (not verified on last run)'}
            </Typography>
          )}
          <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={1}>
            {typeof rowCount === 'number' && (
              <Typography color="text.secondary" variant="caption">
                {rowCount.toLocaleString()} rows
              </Typography>
            )}
            {dataset.dryRun && (
              <>
                <Typography color="text.secondary" variant="caption">
                  {formatBytes(dataset.dryRun.bytesProcessed)} scanned
                </Typography>
                <Typography color="text.secondary" variant="caption">
                  dry run {formatAge(dataset.dryRun.at)}
                </Typography>
              </>
            )}
          </Stack>
        </>
      )}

      {restricted && (
        <Alert severity="warning" variant="outlined">
          Restricted. This card&rsquo;s data is blocked; the canvas shows Restricted instead of a preview.
        </Alert>
      )}

      {mergeBanner && <Box>{mergeBanner}</Box>}
    </Stack>
  );
}
